import React from 'react'
// libs
import moment from 'moment'
// hooks
import { useApolloClient } from 'hooks/useApolloClient'
// graphql
import { CHART_DATA_QUERY } from 'graphql/chart/queries'
// constants
import { CHART_REFRESH_INTERVAL } from 'constants/charts'
// types
import { IChartData, ChartDataStatus } from 'types/graphql'
// utils
import { minBy, pick } from 'lodash'

// How often the time since the last refresh and the Refresh lock are looked at again
const TICK_INTERVAL = 30 * 1000

// As the cache returns it: complete, or none (a cache-only watch returns no partial data)
type Snapshot = Partial<Pick<IChartData, 'computedAt' | 'status'>>

// The charts' numbers as their tiles loaded them: when they were computed, and which ones Refresh may recompute.
// Numbers computed in the last CHART_REFRESH_INTERVAL, or still being computed, are left alone
export const useChartsRefresh = (chartIds: string[]) => {
  const { client } = useApolloClient()

  const [snapshots, setSnapshots] = React.useState<Record<string, Snapshot | undefined>>({})
  const [now, setNow] = React.useState(Date.now)

  const chartIdsKey = chartIds.join()

  React.useEffect(() => {
    // From the cache only: the tiles fetch their numbers themselves
    const subscriptions = chartIds.map((chartId) =>
      client
        .watchQuery<{ chartData: IChartData }>({
          query: CHART_DATA_QUERY,
          variables: { chartId },
          fetchPolicy: 'cache-only'
        })
        // The charts no longer watched are dropped on the way
        .subscribe(({ data }) => setSnapshots((prev) => ({ ...pick(prev, chartIds), [chartId]: data?.chartData })))
    )

    return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
  }, [chartIdsKey])

  React.useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), TICK_INTERVAL)

    return () => clearInterval(interval)
  }, [])

  // A chart not loaded yet is the server's to skip: it has no numbers to recompute
  const isLocked = (snapshot?: Snapshot) =>
    !!snapshot &&
    // COMPUTING and REFRESHING: a Refresh would only join the job already running
    (snapshot.status !== ChartDataStatus.Ready || now - Date.parse(snapshot.computedAt) < CHART_REFRESH_INTERVAL)

  const refreshableIds = chartIds.filter((chartId) => !isLocked(snapshots[chartId]))

  // Of several charts, the oldest numbers: every chart is at least that fresh. One still COMPUTING has none yet
  const computedAt = minBy(chartIds.map((chartId) => snapshots[chartId]?.computedAt).filter(Boolean), (value) =>
    Date.parse(value)
  )

  // Never "in a few seconds": the server's clock may run a little ahead
  const computedAgo = computedAt && moment(Math.min(Date.parse(computedAt), now)).from(now)

  return { refreshableIds, computedAgo }
}
